import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  setPositionOnEdit,
  updatePosition,
} from "../../Redux/Actions/pozicioneActions";

const API_URL = "https://api.arkiva.gov.al/api/open-positions";

const DOCUMENTS_URL = "https://arkiva.gov.al/Assets/Documents";

const EditPosition = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const loggedUser = useSelector((state) => state.users.loggedUser);
  const isLoggedIn = Object.keys(loggedUser || {}).length > 0;

  const [title, setTitle] = useState("");
  const [announceDate, setAnnounceDate] = useState("");
  const [finalDate, setFinalDate] = useState("");
  const [link, setLink] = useState("");

  const [existingFile, setExistingFile] = useState("");
  const [newFile, setNewFile] = useState(null);

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login", { replace: true });
    }
  }, [isLoggedIn, navigate]);

  const generateSlug = (input) => {
    return input
      .toLowerCase()
      .trim()
      .replace(/ë/g, "e")
      .replace(/ç/g, "c")
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const slug = useMemo(() => generateSlug(title), [title]);

  const formatDateForInput = (dateValue) => {
    if (!dateValue) {
      return "";
    }

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();

    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    if (!isLoggedIn || !id) {
      return;
    }

    const fetchPosition = async () => {
      try {
        setLoading(true);
        setErrors({});

        const response = await axios.get(`${API_URL}/${id}`);

        const position = response.data;

        setTitle(position.title || "");
        setAnnounceDate(formatDateForInput(position.announceDate));
        setFinalDate(formatDateForInput(position.finalDate));
        setLink(position.link || "");
        setExistingFile(position.file || "");
      } catch (error) {
        console.error("Error fetching position:", error);

        setErrors({
          submit:
            error.response?.data?.message ||
            "Pozicioni vakant nuk mund të ngarkohej.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPosition();
  }, [id, isLoggedIn]);

  const existingFileUrl = existingFile
    ? `${DOCUMENTS_URL}/${encodeURIComponent(existingFile)}`
    : "";

  const newFilePreviewUrl = useMemo(() => {
    if (!newFile) {
      return "";
    }

    return URL.createObjectURL(newFile);
  }, [newFile]);

  useEffect(() => {
    return () => {
      if (newFilePreviewUrl) {
        URL.revokeObjectURL(newFilePreviewUrl);
      }
    };
  }, [newFilePreviewUrl]);

  const clearError = (fieldName) => {
    setErrors((previousErrors) => {
      if (!previousErrors[fieldName]) {
        return previousErrors;
      }

      const updatedErrors = { ...previousErrors };
      delete updatedErrors[fieldName];

      return updatedErrors;
    });
  };

  const isValidDate = (dateValue) => {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dateValue);

    if (!match) {
      return false;
    }

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);

    const date = new Date(year, month - 1, day);

    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  };

  const parseDate = (dateValue) => {
    const [day, month, year] = dateValue.split("/").map(Number);

    return new Date(year, month - 1, day);
  };

  const isValidUrl = (value) => {
    if (!value) {
      return true;
    }

    try {
      const parsedUrl = new URL(value);

      return ["http:", "https:"].includes(parsedUrl.protocol);
    } catch {
      return false;
    }
  };

  const validate = () => {
    const validationErrors = {};

    const trimmedTitle = title.trim();
    const trimmedAnnounceDate = announceDate.trim();
    const trimmedFinalDate = finalDate.trim();
    const trimmedLink = link.trim();

    if (!trimmedTitle) {
      validationErrors.title = "Titulli është i detyrueshëm.";
    }

    if (!slug) {
      validationErrors.title =
        "Titulli duhet të përmbajë karaktere të vlefshme.";
    }

    if (!trimmedAnnounceDate) {
      validationErrors.announceDate = "Data e shpalljes është e detyrueshme.";
    } else if (!isValidDate(trimmedAnnounceDate)) {
      validationErrors.announceDate =
        "Data e shpalljes duhet të jetë në formatin dd/mm/yyyy.";
    }

    if (trimmedFinalDate) {
      if (!isValidDate(trimmedFinalDate)) {
        validationErrors.finalDate =
          "Data e përfundimit duhet të jetë në formatin dd/mm/yyyy.";
      } else if (
        isValidDate(trimmedAnnounceDate) &&
        parseDate(trimmedFinalDate) < parseDate(trimmedAnnounceDate)
      ) {
        validationErrors.finalDate =
          "Data e përfundimit nuk mund të jetë para datës së shpalljes.";
      }
    }

    if (trimmedLink && !isValidUrl(trimmedLink)) {
      validationErrors.link =
        "Vendosni një link të vlefshëm që fillon me http:// ose https://.";
    }

    if (newFile) {
      const extension = newFile.name.split(".").pop()?.toLowerCase();

      if (extension !== "pdf") {
        validationErrors.file = "Lejohen vetëm dokumente PDF.";
      }

      const maxFileSize = 20 * 1024 * 1024;

      if (newFile.size > maxFileSize) {
        validationErrors.file =
          "Dokumenti nuk mund të jetë më i madh se 20 MB.";
      }
    }

    setErrors(validationErrors);

    return Object.keys(validationErrors).length === 0;
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;

    setNewFile(selectedFile);
    clearError("file");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting || !validate()) {
      return;
    }

    const formData = new FormData();

    formData.append("title", title.trim());
    formData.append("announceDate", announceDate.trim());
    formData.append("finalDate", finalDate.trim());
    formData.append("slug", slug);
    formData.append("link", link.trim());

    if (newFile) {
      formData.append("file", newFile, newFile.name);
    }
    console.log(formData.announceDate);
    console.log(formData.finalDate);

    try {
      setIsSubmitting(true);
      setErrors({});
      const response = await axios.put(`${API_URL}/${id}`, formData);

      dispatch(updatePosition(response.data));
      dispatch(setPositionOnEdit({}));

      navigate("/manage/pozicione-vakante", {
        replace: true,
      });
    } catch (error) {
      console.error("Error updating position:", error);

      const serverErrors = error.response?.data?.errors;

      if (serverErrors && typeof serverErrors === "object") {
        setErrors({
          ...serverErrors,
          submit:
            error.response?.data?.message ||
            "Pozicioni vakant nuk mund të ndryshohej.",
        });
      } else {
        setErrors({
          submit:
            error.response?.data?.message ||
            "Ndodhi një gabim gjatë ndryshimit të pozicionit vakant.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/manage/pozicione-vakante");
    dispatch(setPositionOnEdit({}));
  };

  if (!isLoggedIn) {
    return null;
  }

  return (
    <div className="edit-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="return">
        <button type="button" onClick={handleCancel}>
          <i className="fa-solid fa-chevron-left"></i>
        </button>
      </div>

      <p>Ndrysho Pozicion Vakant</p>

      <form onSubmit={handleSubmit} encType="multipart/form-data">
        <div className="form-group">
          <label htmlFor="title">Titulli</label>

          <input
            type="text"
            id="title"
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              clearError("title");
            }}
            disabled={isSubmitting}
          />

          {errors.title && <small className="error">{errors.title}</small>}
        </div>

        <div className="form-group">
          <label htmlFor="link">Linku</label>

          <input
            type="url"
            id="link"
            value={link}
            placeholder="https://example.com"
            onChange={(event) => {
              setLink(event.target.value);
              clearError("link");
            }}
            disabled={isSubmitting}
          />

          {errors.link && <small className="error">{errors.link}</small>}
        </div>

        <div className="form-group">
          <label htmlFor="announceDate">Data e shpalljes</label>

          <input
            type="text"
            id="announceDate"
            value={announceDate}
            placeholder="dd/mm/yyyy"
            maxLength={10}
            onChange={(event) => {
              setAnnounceDate(event.target.value);
              clearError("announceDate");
            }}
            disabled={isSubmitting}
          />

          {errors.announceDate && (
            <small className="error">{errors.announceDate}</small>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="finalDate">Data e përfundimit</label>

          <input
            type="text"
            id="finalDate"
            value={finalDate}
            placeholder="dd/mm/yyyy"
            maxLength={10}
            onChange={(event) => {
              setFinalDate(event.target.value);
              clearError("finalDate");
            }}
            disabled={isSubmitting}
          />

          {errors.finalDate && (
            <small className="error">{errors.finalDate}</small>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="slug">Slug</label>

          <input type="text" id="slug" value={slug} readOnly />
        </div>

        {existingFile && !newFile && (
          <div className="form-group">
            <label>Dokumenti aktual</label>

            <div className="pdf-preview">
              <iframe
                height={500}
                width={650}
                src={existingFileUrl}
                title="Dokumenti aktual"
                className="pdf-preview-frame"
              />
            </div>

            <a href={existingFileUrl} target="_blank" rel="noopener noreferrer">
              Hap dokumentin në një dritare të re
            </a>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="file">Ndrysho dokumentin</label>

          <input
            type="file"
            id="file"
            name="file"
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            disabled={isSubmitting}
          />

          {errors.file && <small className="error">{errors.file}</small>}
        </div>

        {newFile && newFilePreviewUrl && (
          <div className="form-group">
            <label>Dokumenti i ri</label>

            <small className="selected-file">Dokumenti: {newFile.name}</small>

            <div className="pdf-preview">
              <iframe
                height={500}
                width={650}
                src={newFilePreviewUrl}
                title={`Dokumenti i ri ${newFile.name}`}
                className="pdf-preview-frame"
              />
            </div>
          </div>
        )}

        {errors.submit && (
          <div className="error submit-error">{errors.submit}</div>
        )}

        <div className="edit-actions">
          <button type="submit" className="save" disabled={isSubmitting}>
            {isSubmitting ? "Duke ruajtur..." : "Ruaj ndryshimet"}
          </button>

          <button
            type="button"
            className="cancel"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Anullo
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditPosition;
